import React, { useState, useMemo } from "react";
import generateAntibodyDatabase from "./generateAntibodyDatabase"; // generator should now include lastUsageDate (YYYY-MM-DD)

export default function AntibodySearchDatabase() {
  const [data] = useState(() => generateAntibodyDatabase(200));
  const [selectedAntibody, setSelectedAntibody] = useState(null);
  const [selectedUsage, setSelectedUsage] = useState(null);
  const [searchProtein, setSearchProtein] = useState("");
  const [usageSearch, setUsageSearch] = useState("");
  const [sortAntibody, setSortAntibody] = useState({ column: "protein", asc: true });
  const [sortUsage, setSortUsage] = useState({ column: "usageDate", asc: true });

  // ----- Column definitions with readable labels -----
  const antibodyColumns = [
    { key: "protein", label: "Protein" },
    { key: "brand", label: "Brand" },
    { key: "lotNumber", label: "Lot #" },
    { key: "inLabAvailability", label: "Available" },
    { key: "inLabDataAvailability", label: "Data?" },
    { key: "lastUsageDate", label: "Last Used" } // stores actual date
  ];

  const usageColumns = [
    { key: "usedBy", label: "User" },
    { key: "usageDate", label: "Date" },
    { key: "application", label: "App" },
    { key: "dilution", label: "Dilution" },
    { key: "cellLine", label: "Cell Line" },
    { key: "notes", label: "Notes" }
  ];

  // ----- relative time helper -----
  const relativeRecency = (dateStr) => {
    const now = new Date();
    const then = new Date(dateStr);
    const diffMs = now - then;


    const diffDays = diffMs / (1000 * 60 * 60 * 24);



    let toRet;
    if (diffDays < 7){
      toRet =  `${Math.floor(diffDays)} days ago`;
    } else if (diffDays < 30){
      toRet = `${Math.floor(diffDays / 7)} weeks ago`;
    } else if (diffDays < 365){
      toRet = `${Math.floor(diffDays / 30)} months ago`;
    } else {
      toRet = `${Math.floor(diffDays / 365)} years ago`;
    }

    //console.log(dateStr, then);
    //console.log("DAYS: " + diffDays, toRet);


    return toRet;
  };

  // ----- filtered & sorted antibodies -----
  const filteredAntibodies = useMemo(() => {
    let list = data.filter((ab) =>
      ab.protein.toLowerCase().includes(searchProtein.toLowerCase())
    );

    list.sort((a, b) => {
      if (sortAntibody.column === "lastUsageDate") {
        const valA = new Date(a.lastUsageDate);
        const valB = new Date(b.lastUsageDate);
        return sortAntibody.asc ? valA - valB : valB - valA;
      } else {
        let valA = a[sortAntibody.column];
        let valB = b[sortAntibody.column];
        if (typeof valA === "string") valA = valA.toLowerCase();
        if (typeof valB === "string") valB = valB.toLowerCase();
        if (valA < valB) return sortAntibody.asc ? -1 : 1;
        if (valA > valB) return sortAntibody.asc ? 1 : -1;
        return 0;
      }
    });

    return list;
  }, [data, searchProtein, sortAntibody]);

  // ----- sorted & filtered usages -----
  const sortedUsages = useMemo(() => {
    if (!selectedAntibody) return [];
    let list = [...selectedAntibody.usages]
      .filter((u) =>
        Object.values(u).some((val) =>
          val.toString().toLowerCase().includes(usageSearch.toLowerCase())
        )
      );

    list.sort((a, b) => {
      let valA = a[sortUsage.column];
      let valB = b[sortUsage.column];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortUsage.asc ? -1 : 1;
      if (valA > valB) return sortUsage.asc ? 1 : -1;
      return 0;
    });
    return list;
  }, [selectedAntibody, usageSearch, sortUsage]);

  const toggleSort = (table, column) => {
    if (table === "antibody") {
      setSortAntibody((prev) => ({
        column,
        asc: prev.column === column ? !prev.asc : true
      }));
    } else if (table === "usage") {
      setSortUsage((prev) => ({
        column,
        asc: prev.column === column ? !prev.asc : true
      }));
    }
  };

  // ----- Styles -----
  const containerStyle = { display: "flex", gap: "20px", padding: "20px", fontFamily: "Arial, sans-serif", height: "100vh", flex:1 };
  const panelStyle = { flex: 1, backgroundColor: "#fafafa", padding: "10px", borderRadius: "6px", boxShadow: "0 2px 5px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column" };
  const tableStyle = { width: "100%", borderCollapse: "collapse" };
  const thStyle = { backgroundColor: "#007BFF", color: "white", padding: "8px", textAlign: "left", cursor: "pointer", borderRadius: "4px 4px 0 0" };
  const tdStyle = { padding: "8px", borderBottom: "1px solid #ddd" };
  const inputStyle = { width: "100%", padding: "6px 10px", marginBottom: "10px", borderRadius: "4px", border: "1px solid #ccc", boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)" };
  const selectedRowStyle = { backgroundColor: "#def" };
  const imageStyle = { width: "200px", height: "auto", border: "1px solid #ccc", borderRadius: "4px", boxShadow: "2px 2px 6px rgba(0,0,0,0.1)", transition: "transform 0.2s", cursor: "pointer" };
  const tableContainerStyle = { overflowY: "auto", flex: 1, minHeight: 0 }; // independent scroll

  return (
    <div style={containerStyle}>
      {/* LEFT: Antibody Table */}
      <div style={panelStyle}>
        <h2>Antibodies</h2>
        <input
          type="text"
          placeholder="Search protein..."
          value={searchProtein}
          onChange={(e) => setSearchProtein(e.target.value)}
          style={inputStyle}
        />
        <div style={tableContainerStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {antibodyColumns.map((col) => (
                  <th key={col.key} style={thStyle} onClick={() => toggleSort("antibody", col.key)}>
                    {col.label} {sortAntibody.column === col.key ? (sortAntibody.asc ? "▲" : "▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAntibodies.map((ab, idx) => (
                <tr
                  key={idx}
                  onClick={() => { setSelectedAntibody(ab); setSelectedUsage(null); }}
                  style={selectedAntibody === ab ? selectedRowStyle : {}}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#f1f1f1"}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = selectedAntibody === ab ? "#def" : "white"}
                >
                  {antibodyColumns.map((col) => (
                    <td key={col.key} style={tdStyle}>
                      {col.key === "inLabAvailability" || col.key === "inLabDataAvailability"
                        ? ab[col.key] ? "Yes" : "No"
                        : col.key === "lastUsageDate"
                          ? relativeRecency(ab.lastUsageDate)
                          : ab[col.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* MIDDLE: Usage Table */}
      <div style={panelStyle}>
        <h2>Usages</h2>
        {selectedAntibody ? (
          <>
            <input
              type="text"
              placeholder="Search usages..."
              value={usageSearch}
              onChange={(e) => setUsageSearch(e.target.value)}
              style={inputStyle}
            />
            <div style={tableContainerStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {usageColumns.map((col) => (
                      <th key={col.key} style={thStyle} onClick={() => toggleSort("usage", col.key)}>
                        {col.label} {sortUsage.column === col.key ? (sortUsage.asc ? "▲" : "▼") : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedUsages.map((u, idx) => (
                    <tr
                      key={idx}
                      onClick={() => {setSelectedUsage(u);console.log(u)}}
                      style={selectedUsage === u ? selectedRowStyle : {}}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#f1f1f1"}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = selectedUsage === u ? "#def" : "white"}
                    >
                      {usageColumns.map((col) => (
                        <td key={col.key} style={tdStyle}>{u[col.key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p>Select an antibody to see usages.</p>
        )}
      </div>

      {/* RIGHT: Images */}
      <div style={panelStyle}>
        <h2>Images / Data</h2>
        {selectedUsage ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", overflowY: "auto", maxHeight: "80vh" }}>
            {selectedUsage.images.map((src, idx) => (
              <img key={idx} src={src} alt={`usage-img-${idx}`} style={imageStyle} />
            ))}
          </div>
        ) : (
          <p>Select a usage to see images.</p>
        )}
      </div>
    </div>
  );
}
